import { generatePath } from '@cibo/core'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import ArrowDropUp from '@mui/icons-material/ArrowDropUp'
import { Box, Button, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { useInventoryTargets } from 'features/Inventories/queries/useInventoryTargets'
import { SCOPE_THREE_ROUTES } from 'navigation/types/scopeThreeRoutes'
import numeral from 'numeral'
import { CSSProperties, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

export const Targets = () => {
  const { inventoryId } = useParams()
  const { data: targets, isLoading: targetsLoading } = useInventoryTargets(inventoryId)
  const { t } = useTranslation('impact/ScopeThreeTargets')
  const { palette } = useTheme()
  const navigate = useNavigate()

  if (targetsLoading) {
    return <Skeleton height="264px" width="100%" />
  }

  if (!targets?.absoluteEmissions?.reducePer) {
    return (
      <Box
        sx={{
          width: '100%',
          padding: 3,
          marginBottom: 1,
          backgroundColor: 'background.default',
          backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='${palette.divider}' stroke-width='1.5' stroke-dasharray='4%2c 4' stroke-dashoffset='4' stroke-linecap='square'/%3e%3c/svg%3e")`,
          borderRadius: '12px',
        }}
      >
        <Stack spacing={1} alignItems="center">
          <Typography>{t('setTargetPrompt')}</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            color="secondary"
            onClick={() => navigate(generatePath(SCOPE_THREE_ROUTES.TARGETS, { inventoryId }))}
          >
            {t('setTargets')}
          </Button>
        </Stack>
      </Box>
    )
  }

  const rows = [
    {
      name: t('emissions'),
      unit: t('tCo2eq'),
      baseline: targets.totalCo2EmissionsBaseLine,
      current: targets.totalCo2EmissionsCurrent,
      target: (1 - targets.absoluteEmissions.reducePer / 100) * targets.totalCo2EmissionsBaseLine,
    },
    !!targets.removals && {
      name: t('removals'),
      unit: t('tCo2eq'),
      baseline: t('n/a'),
      current: targets.totalCo2RemovalsCurrent,
      target: targets.removals?.removeTonnes,
    },
    !!targets.carbonIntensity?.find(a => a.crop === 'corn') && {
      name: t('carbonIntensityCorn'),
      unit: t('intensityKg'),
      baseline: targets.carbonIntensities?.corn,
      current: targets.carbonIntensities?.currentCorn,
      target: targets.carbonIntensity?.find(target => target.crop === 'corn')?.reduceTo,
    },
    !!targets.carbonIntensity?.find(a => a.crop === 'soybeans') && {
      name: t('carbonIntensitySoy'),
      unit: t('intensityKg'),
      baseline: targets.carbonIntensities?.soybeans,
      current: targets.carbonIntensities?.currentSoybeans,
      target: targets.carbonIntensity?.find(target => target.crop === 'soybeans')?.reduceTo,
    },
  ].filter(Boolean)

  return (
    <Stack spacing={1.5} alignItems="start">
      <table>
        <tbody>
          <tr>
            <th></th>
            <TargetTableHeader>{t('baseline')}</TargetTableHeader>
            <TargetTableHeader> {t('current')}</TargetTableHeader>
            <TargetTableHeader>{t('target')}</TargetTableHeader>
          </tr>
          {rows.map(
            (row, index) =>
              !!row && (
                <tr key={index}>
                  <th scope="row" style={{ paddingRight: '12px', paddingBottom: '12px' }}>
                    <Stack direction="column" alignItems="start">
                      <Typography variant="body1" sx={{ textAlign: 'start' }}>
                        {row.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {row.unit}
                      </Typography>
                    </Stack>
                  </th>
                  <TargetTableCell>
                    <Typography>
                      {!row.baseline || Number.isNaN(+row.baseline)
                        ? row.baseline
                        : numeral(row.baseline).format(
                            row.unit === t('intensityKg') ? '0.00' : '0,0'
                          )}
                    </Typography>
                    {row.baseline !== t('n/a') && <PercentIndicator />}
                  </TargetTableCell>
                  <TargetTableCell style={{ textAlign: 'center' }}>
                    <Typography>
                      {numeral(row.current).format(row.unit === t('intensityKg') ? '0.00' : '0,0')}
                    </Typography>
                    {row.baseline !== t('n/a') && (
                      <PercentIndicator
                        dividend={row.current}
                        baseline={row.baseline}
                        displayAsColor
                      />
                    )}
                  </TargetTableCell>
                  <TargetTableCell>
                    <Typography>
                      {numeral(row.target).format(row.unit === t('intensityKg') ? '0.00' : '0,0')}
                    </Typography>

                    {row.baseline !== t('n/a') && (
                      <PercentIndicator dividend={row.target} baseline={row.baseline} />
                    )}
                  </TargetTableCell>
                </tr>
              )
          )}
        </tbody>
      </table>

      <Button
        variant="outlined"
        color="secondary"
        onClick={() => navigate(generatePath(SCOPE_THREE_ROUTES.TARGETS, { inventoryId }))}
      >
        {t('adjustTargets')}
      </Button>
    </Stack>
  )
}

const PercentIndicator = ({
  dividend,
  baseline,
  displayAsColor = false,
}: {
  dividend?: number
  baseline?: number | string
  displayAsColor?: boolean
}) => {
  const { t } = useTranslation('impact/ScopeThreeTargets')
  if (
    dividend === 0 ||
    baseline === 0 ||
    Number.isNaN(dividend) ||
    Number.isNaN(baseline) ||
    typeof dividend !== 'number' ||
    typeof baseline !== 'number'
  ) {
    return (
      <Stack direction="row" alignItems="center">
        <Box width={24} height={24} />
      </Stack>
    )
  }
  const percentValueAsFloat = dividend / baseline
  const changeNumber = Math.abs(1 - percentValueAsFloat) * 100
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={
        displayAsColor
          ? { color: percentValueAsFloat < 1 ? 'success.main' : 'error.main' }
          : { color: 'text.secondary' }
      }
    >
      {percentValueAsFloat < 1 ? <ArrowDropDown /> : <ArrowDropUp />}
      <Typography variant="caption" sx={{ color: 'inherit' }}>
        {t('percent', { number: changeNumber })}
      </Typography>
    </Stack>
  )
}

const TargetTableHeader: FC<{ style?: CSSProperties }> = ({ children, style, ...props }) => {
  const { palette } = useTheme()
  return (
    <th scope="col" style={{ paddingRight: '12px', paddingBottom: '12px', ...style }} {...props}>
      <Typography style={{ borderBottom: `1px solid ${palette.divider}` }}>{children}</Typography>
    </th>
  )
}
const TargetTableCell: FC<{ style?: CSSProperties }> = ({ children, style, ...props }) => (
  <th scope="row" style={{ paddingRight: '12px', paddingBottom: '12px' }}>
    <Stack direction="column" alignItems="start">
      {children}
    </Stack>
  </th>
)
